import React, { createContext, useContext, useState } from 'react';

import { WagmiConfig } from 'wagmi';

import { client } from './wagmi';

const { innerWidth: width } = window;
const firstRunCheck = localStorage.getItem('firstRun') === '1' ? false : true;

export interface AppContext {
  debug: string[];
  config: any;
  favorites: any;
  reminders: any;
  whales: any;
  preparing: boolean;
  address: string;
  signer: any;
  signed: boolean;
  ensCount: any;
  nftCount: any;
  wallets: any;
  connectedWallets: any;
  customSearch: boolean;
  portfolioStatus: string;
  tokenDetails: any;

  tokensEth: any;
  tokensBsc: any;
  tokensPolygon: any;
  tokensAvalanche: any;
  tokensMoonbeam: any;
  tokensMoonriver: any;
  tokensMantle: any;

  hideMinimumWallet: number;
  hideMinimumToken: number;

  nft: any;
  allow: boolean;
  isMobile: boolean;
  demoMode: boolean;
  systemReady: boolean;
  tutorialCount: number;
  firstRun: number;
}

export const initialState: AppContext = {
  debug: [],
  config: '',
  vestingCalendars: '',
  favorites: '',
  reminders: '',
  whales: '',
  preparing: false,
  address: '',
  signer: null,
  signed: false,
  ensCount: '',
  nftCount: '',
  wallets: '',
  connectedWallets: '',
  customSearch: true,
  portfolioStatus: 'up',
  tokenDetails: '',

  tokensEth: '',
  tokensBsc: '',
  tokensPolygon: '',
  tokensAvalanche: '',
  tokensMoonbeam: '',
  tokensMoonriver: '',
  tokensMantle: '',

  hideMinimumWallet: 0,
  hideMinimumToken: 0,

  nft: '',
  allow: false,
  isMobile: width < 1025 ? true : false,
  demoMode: false,
  systemReady: false,
  tutorialCount: 0,
  firstRun: firstRunCheck,
};

export interface UpdateAppContext {
  setDebug: (value: string[]) => void;
  setConfig: (value: any) => void;
  setVestingCalendars: (value: any) => void;
  setFavorites: (value: any) => void;
  setReminders: (value: any) => void;
  setWhales: (value: any) => void;
  setPreparing: (value: boolean) => void;
  setAddress: (value: string) => void;
  setSigner: (value: any) => void;
  setSigned: (value: boolean) => void;
  setEnsCount: (value: any) => void;
  setNftCount: (value: any) => void;
  setWallets: (value: any) => void;
  setConnectedWallets: (value: any) => void;
  setCustomSearch: (value: boolean) => void;
  setPortfolioStatus: (value: string) => void;
  setTokenDetails: (value: any) => void;

  setTokensEth: (value: any) => void;
  setTokensBsc: (value: any) => void;
  setTokensPolygon: (value: any) => void;
  setTokensAvalanche: (value: any) => void;
  setTokensMoonbeam: (value: any) => void;
  setTokensMoonriver: (value: any) => void;
  setTokensMantle: (value: any) => void;

  setHideMinimumWallet: (value: number) => void;
  setHideMinimumToken: (value: number) => void;

  setNft: (value: any) => void;
  setAllow: (value: boolean) => void;
  setIsMobile: (value: boolean) => void;
  setDemoMode: (value: boolean) => void;
  setSystemReady: (value: boolean) => void;
  setTutorialCount: (value: number) => void;
  setFirstRun: (value: boolean) => void;
}

const GlobalContext = createContext<AppContext>(initialState);

const UpdateGlobalContext = createContext<UpdateAppContext>({
  setDebug: (_value: string[]) => {
    return;
  },
  setConfig: (_value: any) => {
    return;
  },
  setVestingCalendars: (_value: any) => {
    return;
  },
  setFavorites: (_value: any) => {
    return;
  },
  setReminders: (_value: any) => {
    return;
  },
  setWhales: (_value: any) => {
    return;
  },
  setPreparing: (_value: boolean) => {
    return;
  },
  setAddress: (_value: string) => {
    return;
  },
  setSigner: (_value: any) => {
    return;
  },
  setSigned: (_value: boolean) => {
    return;
  },
  setEnsCount: (_value: any) => {
    return;
  },
  setNftCount: (_value: any) => {
    return;
  },
  setWallets: (_value: any) => {
    return;
  },
  setConnectedWallets: (_value: any) => {
    return;
  },
  setCustomSearch: (_value: boolean) => {
    return;
  },
  setPortfolioStatus: (_value: string) => {
    return;
  },
  setTokenDetails: (_value: any) => {
    return;
  },

  setTokensEth: (_value: any) => {
    return;
  },
  setTokensBsc: (_value: any) => {
    return;
  },
  setTokensPolygon: (_value: any) => {
    return;
  },
  setTokensAvalanche: (_value: any) => {
    return;
  },
  setTokensMoonbeam: (_value: any) => {
    return;
  },
  setTokensMoonriver: (_value: any) => {
    return;
  },
  setTokensMantle: (_value: any) => {
    return;
  },

  setHideMinimumWallet: (_value: number) => {
    return;
  },
  setHideMinimumToken: (_value: number) => {
    return;
  },

  setNft: (_value: any) => {
    return;
  },
  setAllow: (_value: boolean) => {
    return;
  },
  setIsMobile: (_value: boolean) => {
    return;
  },
  setDemoMode: (_value: boolean) => {
    return;
  },
  setSystemReady: (_value: boolean) => {
    return;
  },
  setTutorialCount: (_value: number) => {
    return;
  },
  setFirstRun: (_value: boolean) => {
    return;
  },
});

export function GlobalProvider({ children }: { children?: React.ReactChild | React.ReactChild[] }) {
  const [debug, setDebug] = useState<any>(initialState.debug);
  const [config, setConfig] = useState<any>(initialState.config);
  const [vestingCalendars, setVestingCalendars] = useState<any>(initialState.vestingCalendars);
  const [favorites, setFavorites] = useState<any>(initialState.favorites);
  const [reminders, setReminders] = useState<any>(initialState.reminders);
  const [whales, setWhales] = useState<any>(initialState.whales);
  const [preparing, setPreparing] = useState<boolean>(initialState.preparing);
  const [address, setAddress] = useState<string>(initialState.address);
  const [signer, setSigner] = useState<any>(initialState.signer);
  const [signed, setSigned] = useState<any>(initialState.signed);

  const [ensCount, setEnsCount] = useState<any>(initialState.ensCount);
  const [nftCount, setNftCount] = useState<any>(initialState.nftCount);
  const [wallets, setWallets] = useState<any>(initialState.wallets);
  const [connectedWallets, setConnectedWallets] = useState<any>(initialState.connectedWallets);
  const [customSearch, setCustomSearch] = useState<boolean>(initialState.customSearch);
  const [portfolioStatus, setPortfolioStatus] = useState<string>(initialState.portfolioStatus);

  const [tokenDetails, setTokenDetails] = useState<any>(initialState.tokenDetails);

  const [tokensEth, setTokensEth] = useState<any>(initialState.tokensEth);
  const [tokensBsc, setTokensBsc] = useState<any>(initialState.tokensBsc);
  const [tokensPolygon, setTokensPolygon] = useState<any>(initialState.tokensPolygon);
  const [tokensAvalanche, setTokensAvalanche] = useState<any>(initialState.tokensAvalanche);
  const [tokensMoonbeam, setTokensMoonbeam] = useState<any>(initialState.tokensMoonbeam);
  const [tokensMoonriver, setTokensMoonriver] = useState<any>(initialState.tokensMoonriver);
  const [tokensMantle, setTokensMantle] = useState<any>(initialState.tokensMantle);

  const [hideMinimumWallet, setHideMinimumWallet] = useState<number>(
    initialState.hideMinimumWallet,
  );

  const [hideMinimumToken, setHideMinimumToken] = useState<number>(initialState.hideMinimumToken);

  const [nft, setNft] = useState<any>(initialState.nft);
  const [allow, setAllow] = useState<boolean>(initialState.allow);
  const [isMobile, setIsMobile] = useState<boolean>(initialState.isMobile);
  const [demoMode, setDemoMode] = useState<boolean>(initialState.demoMode);
  const [systemReady, setSystemReady] = useState<boolean>(initialState.systemReady);
  const [tutorialCount, setTutorialCount] = useState<boolean>(initialState.tutorialCount);
  const [firstRun, setFirstRun] = useState<boolean>(initialState.firstRun);
  const [theme, setTheme] = useState<'light' | 'dark'>('light');


  return (
    <WagmiConfig client={client}>
      <GlobalContext.Provider
        value={{
          debug,
          config,
          vestingCalendars,
          favorites,
          reminders,
          whales,
          preparing,
          address,
          signer,
          signed,
          ensCount,
          nftCount,
          wallets,
          connectedWallets,
          customSearch,
          portfolioStatus,
          tokenDetails,

          tokensEth,
          tokensBsc,
          tokensPolygon,
          tokensAvalanche,
          tokensMoonbeam,
          tokensMoonriver,
          tokensMantle,
          hideMinimumWallet,
          hideMinimumToken,
          nft,
          allow,
          isMobile,
          demoMode,
          systemReady,
          tutorialCount,
          firstRun,
          theme,
        }}
      >
        <UpdateGlobalContext.Provider
          value={{
            setDebug,
            setConfig,
            setVestingCalendars,
            setFavorites,
            setReminders,
            setWhales,
            setPreparing,
            setAddress,
            setSigner,
            setSigned,
            setEnsCount,
            setNftCount,
            setWallets,
            setConnectedWallets,
            setCustomSearch,
            setPortfolioStatus,
            setTokenDetails,

            setTokensEth,
            setTokensBsc,
            setTokensPolygon,
            setTokensAvalanche,
            setTokensMoonbeam,
            setTokensMoonriver,
            setTokensMantle,
            setHideMinimumWallet,
            setHideMinimumToken,
            setNft,
            setAllow,
            setIsMobile,
            setDemoMode,
            setSystemReady,
            setTutorialCount,
            setFirstRun,
            setTheme,
          }}
        >
          {children}
        </UpdateGlobalContext.Provider>
      </GlobalContext.Provider>
    </WagmiConfig>
  );
}

export function useGlobalContext() {
  const globalContext = useContext(GlobalContext);
  const updateGlobalContext = useContext(UpdateGlobalContext);

  return {
    ...globalContext,
    ...updateGlobalContext,
  };
}
